import styled from 'styled-components';

import { ANIM, LOGIN, LOGIN_MOBILE } from '../../theme/tokens';

/**
 * Login / sign-in page styles — a hand-written, token-only reproduction of the
 * config-driven `module.owners.hub.login` stylesheet. The whole node tree keeps
 * the original class names (`.page`, `.main`, `.body`, `.sign-in-form`, …) and
 * the CSS is nested under the `Login` root exactly as the source SCSS was, so the
 * markup in `index.tsx` stays a 1:1 mirror of the template. All raw colours come
 * from the `LOGIN` token group; the title sweep reuses `LOGIN.titleGradient`.
 * The card is KEPT-DARK: every LOGIN colour it paints with resolves to its dark
 * literal in both themes (see KEPT_DARK_LOGIN in theme/themePalette.ts), so
 * nothing in here is theme-scoped. The `@media (max-width: 768px)` block is the
 * source ModuleElement's own responsive reflow.
 */
export const Login = styled.div`
  width: 100dvw;
  min-height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: ${LOGIN.pageBg};
  background-image: ${LOGIN.bgImage};
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: cover;

  /* Firefox */
  & {
    scrollbar-color: ${LOGIN.scrollColorThumb} ${LOGIN.scrollColorTrack};
  }

  /* Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${LOGIN.scrollTrack};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${LOGIN.scrollThumb};
    border-radius: 10px;
    border: 3px solid ${LOGIN.scrollThumbBorder};
  }

  /* The app sets \`color-scheme: dark\` globally, which flips the UA's default
     form-control text colour to white and turns the white email field
     white-on-white. Force the light UA scheme on text inputs so their text is
     dark again. */
  & input:not([type='checkbox']):not([type='radio']) {
    color-scheme: light;
  }

  /* Vertically centred (per the Figma frame): the logo + card block sits in the
     middle of the viewport. min-height + center keeps it centred on tall screens;
     the vertical padding on .main plus the root's overflow-y:auto lets a viewport
     shorter than the content scroll instead of clipping. The login<->register
     morph re-centres the block as the card height changes. */
  .page {
    width: 100%;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .main {
    width: 480px;
    padding: 48px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    /* 64px between the three groups (logo / card / language selector) per Figma. */
    gap: 64px;
  }

  .header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .logo {
    width: 200px;
    display: flex;

    svg,
    img {
      width: 100%;
      height: auto;
      cursor: pointer;
    }
  }

  /* The card hugs its content: its height is driven by the inner wrapper's
     natural height (set inline by useAuthCardMorph) and eased, so it morphs
     smoothly between the sign-in and sign-up heights and the email-sent view.
     The padding lives on \`.body-inner\` so the measured height already includes
     it. \`flex-shrink: 0\` keeps it from compressing on a short viewport. */
  .body {
    width: 480px;
    flex-shrink: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    /* Top-pinned, NOT centred: at rest the inline morph height (useAuthCardMorph) equals the
       content height so this is invisible, but DURING the height transition it keeps the content
       anchored to the top so only the card's bottom edge grows/shrinks. Centring here made the
       inner content slide vertically for the ~350ms of the morph (the "dance" on mode toggle and
       the login<->register swap). */
    justify-content: flex-start;
    overflow: hidden;
    /* The card's own padding lives on \`.body-inner\` (so the measured morph
       height already includes it); the card itself carries none. */
    padding: 0;
    border-radius: 24px;
    background: ${LOGIN.bodyBg};
    box-shadow: ${LOGIN.cardShadow};
    transition: height ${ANIM.DRAWER_MS}ms ${ANIM.EASE};
  }

  .body-inner {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    padding: 24px;
  }

  @media (prefers-reduced-motion: reduce) {
    .body {
      transition: none;
    }
  }

  .layout {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 64px;
  }

  /* Sign-in (form) view only — the email-sent confirmation keeps the centred base.
     The card is a left-aligned, content-height stack on a uniform 24px rhythm:
     heading / input / button (inside the form) then the divider and footer
     (siblings of the form). All three sit at gap 24; the heading's own title->
     description gap is 8. */
  .wrapper.form-view {
    height: auto;
    justify-content: flex-start;
    align-items: stretch;
    gap: 24px;

    /* The form's gap is the tight input->CTA pairing (12), NOT the card's 24 rhythm.
       The heading is the form's first child, so it makes up the difference itself and
       still sits 24 from the field. */
    .sign-in-form {
      height: auto;
      align-items: stretch;
      gap: 12px;
    }

    .heading {
      align-items: flex-start;
      margin-bottom: 12px;
    }

    /* The expired-magic-link notice is a conditional FOURTH child between the heading
       and the field, so the tightened form gap would otherwise halve its separation
       from the field too. It makes up the difference itself, exactly as the heading
       above does, keeping the notice on the card's 24 rhythm. */
    .link-notice {
      margin-bottom: 12px;
    }

    .title {
      max-width: none;
      text-align: left;
    }

    .subtitle {
      text-align: left;
    }

    .footer {
      justify-content: flex-start;
    }
  }

  /* Email-sent (magic-link) view — the same left-aligned glass card on a 24px
     rhythm: a round back button + heading, the email chip, the paper-plane art,
     a styled divider, then the expiry note. */
  .wrapper.sent-view {
    height: auto;
    justify-content: flex-start;
    align-items: stretch;
    gap: 24px;

    .heading {
      align-items: flex-start;
      gap: 12px;
    }

    .heading-copy {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      width: 100%;
    }

    .title {
      max-width: none;
      text-align: left;
    }

    .subtitle {
      text-align: left;
    }

    /* Round back button (returns to the sign-in form). */
    .back {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 0;
      border-radius: 9999px;
      background: ${LOGIN.cardChipBg};
      color: ${LOGIN.cardChipText};
      cursor: pointer;
      transition: filter ${ANIM.INTERACT_MS}ms ease;

      .bi {
        font-size: 16px;
        display: inline-flex;
      }

      &:hover {
        filter: brightness(1.3);
      }
    }

    /* Pill showing the address the link was sent to (envelope + email). */
    .email-chip {
      align-self: flex-start;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      max-width: 100%;
      height: 40px;
      padding: 8px 16px;
      border-radius: 9999px;
      background: ${LOGIN.cardChipBg};
      color: ${LOGIN.cardChipText};

      .icon {
        font-size: 16px;
        display: inline-flex;
        flex-shrink: 0;
      }
      .email {
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    /* The source illustration (a 381x282 SVG) carries a lot of empty margin: the
       artwork's real bounding box is only x 58-356, y 108-218. So the band still
       crops that margin away, but its height TRACKS its width instead of being a
       fixed 148px: cover's scale is width-driven here (the image always meets the
       band edge to edge horizontally), so a fixed height meant a wider card scaled
       the artwork UP against a frozen band and clipped the plane and its trail.
       With the ratio pinned, the whole illustration scales with the container and
       stays contained at every width. 432x148 is the desktop geometry (480px card
       minus 2x24 padding, and the design's band height); max-width holds the art at
       that design size on the wider fluid card below the breakpoint. */
    .art {
      width: 100%;
      max-width: 432px;
    }

    /* The ratio lives on the image, not on \`.art\`: \`.art\` is a column-flex item,
       so its automatic minimum height would be floored by the image's own intrinsic
       height and the ratio would not hold. */
    .mail-sent {
      display: block;
      width: 100%;
      height: auto;
      aspect-ratio: 432 / 148;
      object-fit: cover;
      /* The artwork's vertical mid-point sits at 58% of the source height; 64%
         parks that mid-point in the middle of the cropped band. */
      object-position: center 64%;
    }

    .warning {
      position: static;
      text-align: left;
      color: ${LOGIN.warning};
      font-size: 12px;
      font-weight: 500;
      line-height: 1.1;
    }
  }

  .sign-in-form {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    gap: 48px;
  }

  .heading {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  /* "Your login link has expired" notice — a red-tinted glass panel that sits
     between the heading and the email field (a child of .sign-in-form, so the
     form's 24px gap rhythm frames it) when the user returns via a lapsed magic
     link. Icon + a light-red headline + a calm grey hint telling them what to do.
     The ResizeObserver in useAuthCardMorph re-measures the card as it appears. */
  .link-notice {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid ${LOGIN.errorBoxBorder};
    background: ${LOGIN.errorBoxBg};
    text-align: left;

    .notice-icon {
      flex-shrink: 0;
      font-size: 16px;
      line-height: 20px;
      color: ${LOGIN.errorBoxIcon};
      display: inline-flex;
    }

    .notice-copy {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .notice-title {
      color: ${LOGIN.errorBoxText};
      font-size: 14px;
      font-weight: 600;
      line-height: 1.35;
    }

    .notice-hint {
      color: ${LOGIN.subtitle};
      font-size: 12px;
      font-weight: 400;
      line-height: 1.4;
    }
  }

  .title {
    max-width: 240px;
    font-size: 36px;
    font-style: normal;
    font-weight: 800;
    line-height: 1.12;
    text-align: center;
    background: ${LOGIN.titleGradient};
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    /* \`background-clip: text\` reveals the gradient only where it is PAINTED, and the
       paint area is this box. Satoshi 800's font box (45px at 36px) is taller than the
       1.12 line box (40.32px), so the tail of a \`g\` dropped ~2.5px below the box and
       was cut off with a flat edge. The padding extends the paint area under the
       descenders; the equal negative margin gives the space back, so the surrounding
       layout (the \`.heading\` gap) is pixel-identical. 4px also covers the 28px mobile
       size, where the same maths leaves ~1.5px outside. */
    padding-bottom: 4px;
    margin-bottom: -4px;
  }

  .subtitle {
    color: ${LOGIN.subtitle};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.43;
    text-align: center;
  }

  .address {
    color: ${LOGIN.address};
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
  }

  .input {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;

    /* \`type='text'\` is included on purpose: the password field flips password->text when the
       show/hide eye is toggled, and without this selector the revealed field falls back to the
       UA's default metrics and visibly shrinks (48px -> ~30px). The email field is type='email';
       the only text-type input under \`.input\` is the revealed password, so this stays scoped. */
    input[type='email'],
    input[type='password'],
    input[type='text'] {
      height: 48px;
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 0 12px;
      border-radius: 999px;
      border: 1px solid ${LOGIN.inputBorder};
      background: ${LOGIN.inputBg};
      outline: 0;

      /* Focus recolours that SAME 1px edge in place, in BOTH themes: the card is
         kept-dark, so the field is one white pill on one near-black card everywhere and
         a theme-scoped focus rule would only re-open the per-theme split this card
         exists to close. Deliberately only the colour: no second ring outside it (an
         outline or a spread shadow would read as a double edge on a pill this round) and
         no width change (that would reflow the text inside a border-box control). The
         edge is the boundary between the white fill and the near-black card, so its
         colour has to clear 3:1 against BOTH; \`LOGIN.inputFocusBorder\` documents why
         zinc-500 is the one that does. */
      transition: border-color ${ANIM.INTERACT_MS}ms ease;

      &:focus-visible {
        border-color: ${LOGIN.inputFocusBorder};
      }

      &::placeholder {
        color: ${LOGIN.inputPlaceholder};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
      }
    }
  }

  /* The password row keeps the email field's own metrics (height/border/background):
     it only adds a relative wrapper so the show/hide button can sit INSIDE the pill
     without affecting the input's box. \`padding-right\` on the field clears the button
     so typed text never runs under it. */
  .input.password-input {
    position: relative;

    /* Both types: the field is password when hidden, text when the eye reveals it, and the
       padding-right (clearing the eye button) must hold in both so typed text never runs
       under the glyph. */
    input[type='password'],
    input[type='text'] {
      padding-right: 44px;
    }

    /* Borderless icon button, absolutely centred on the field's right edge. Understated
       on purpose: a plain text-coloured glyph with a subtle hover lift, no fill/border of
       its own, so it reads as part of the field rather than a second control. */
    .eye-toggle {
      position: absolute;
      top: 50%;
      right: 4px;
      transform: translateY(-50%);
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 0;
      background: transparent;
      color: ${LOGIN.subtitle};
      cursor: pointer;
      transition: color ${ANIM.INTERACT_MS}ms ease;

      .bi {
        font-size: 18px;
        display: inline-flex;
      }

      &:hover {
        color: ${LOGIN.inputPlaceholder};
      }
    }
  }

  /* Sign-in method switch: a compact two-pill segmented control on the card's own
     24px rhythm. Understated per the "start subtle" working agreement, a grey track
     with a slightly lighter active pill, no brand colour yet. */
  .mode-toggle {
    width: 100%;
    display: flex;
    gap: 4px;
    padding: 4px;
    border-radius: 9999px;
    background: ${LOGIN.tipBg};

    .mode-option {
      flex: 1;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 9999px;
      background: transparent;
      color: ${LOGIN.subtitle};
      font-size: 13px;
      font-style: normal;
      font-weight: 500;
      line-height: 1;
      cursor: pointer;
      transition:
        background-color ${ANIM.INTERACT_MS}ms ease,
        color ${ANIM.INTERACT_MS}ms ease;

      &.active {
        background: ${LOGIN.cardChipBg};
        color: ${LOGIN.cardChipText};
      }
    }
  }

  /* Small, right-aligned link under the password row, matching the footer "Register."
     link's colour/weight treatment but at a secondary size (it is a sibling affordance,
     not the card's primary CTA). */
  .forgot-link {
    align-self: flex-end;
    color: ${LOGIN.footerLink};
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 1.2;
    text-decoration: none;
  }

  .cta {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .button {
      width: 100%;
      /* Matches the email field's 48px so the two stack as one control pair rather
         than a tall input above a shorter button. */
      height: 48px;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;
      padding: 0 16px;
      border: 0;
      border-radius: 9999px;
      background: ${LOGIN.cta};
      cursor: pointer;
      pointer-events: all;

      .text {
        color: ${LOGIN.ctaText};
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 20px;
      }

      .icon {
        color: ${LOGIN.ctaText};
        font-size: 20px;
        display: inline-flex;
      }

      /* One disabled treatment for both themes: the card is kept-dark everywhere, so
         the orange fill and black label always composite down against the same
         near-black surface and the plain fade reads as "unavailable" the way it was
         designed to. Light used to override this with an explicit pale pair, because
         back then it faded over a WHITE card and left near-invisible grey copy; that
         override is gone with the white card it existed for. */
      &.disabled,
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    .mail-sent {
      width: 75%;
      margin: -20% 0;
    }
  }

  .warning {
    position: absolute;
    bottom: 0;
    color: ${LOGIN.warning};
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 110%;
  }

  /* The hairline between the CTA and the footer. The shared <Divider variant="styled">
     paints itself from GRADIENTS.dividerStyledH, a WHITE lift shared with every other
     surface in the app, so it follows the theme and inverts to a dark line on light,
     which would vanish on this kept-dark card. Repoint it at the card's own token; the
     shared one stays alone so no other divider in the app is dragged dark with it. */
  .auth-divider {
    background: ${LOGIN.cardDivider};
  }

  .footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    .label {
      color: ${LOGIN.footerLabel};
      font-size: 16px;
      font-style: normal;
      font-weight: 300;
      line-height: 110%;
    }

    .link {
      color: ${LOGIN.footerLink};
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 110%;
      text-decoration: none;
    }
  }

  /* The language switcher is its own component (see
     \`components/LanguagePicker\`); only its responsive margin needs the page
     breakpoint here — the rest of its styling lives with the component. */

  ${LOGIN_MOBILE} {
    &,
    .page,
    .main,
    .body,
    .header,
    .footer {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
    }
    & {
      min-height: 100vh !important;
      height: auto !important;
      overflow-y: auto !important;
      justify-content: flex-start !important;
    }
    .main {
      padding: 24px 12px !important;
      /* The desktop 64px inter-group gap is too airy on a phone. */
      gap: 24px !important;
      overflow-x: hidden !important;
    }
    /* Comfortable card padding while the card still nearly spans the screen. */
    .body-inner {
      padding: 24px 20px !important;
    }
    .wrapper,
    .sign-in-form,
    .heading,
    .input,
    .mode-toggle,
    .cta {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    .heading .title {
      font-size: 28px !important;
      line-height: 1.15 !important;
      word-wrap: break-word !important;
    }
    .heading .subtitle {
      font-size: 14px !important;
      line-height: 1.4 !important;
    }
    .button {
      width: 100% !important;
      max-width: 100% !important;
    }
    .header .logo img {
      max-width: 100% !important;
      height: auto !important;
    }
    /* NB: \`.body\` is deliberately NOT in this auto-height reset — its height is the
       inline px value set by useAuthCardMorph, and an \`height: auto !important\` here
       would beat the inline style and kill the morph on mobile. The card stays
       content-driven (the measured height already reflects the mobile padding) and
       keeps its base \`overflow: hidden\` + height transition so it morphs here too. */
    .main,
    .layout,
    .wrapper,
    .sign-in-form,
    .cta {
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow: visible !important;
      justify-content: flex-start !important;
    }
    /* Vertically centre the logo + card + language block (matches desktop); falls
       back to top-anchored + scroll when the content is taller than the viewport. */
    .page {
      min-height: 100dvh !important;
      height: auto !important;
      overflow: visible !important;
      /* Centred: symmetric breathing where the content fits (balanced spacing between header,
         card and language switcher); a no-op under overflow, where the column scrolls from the
         top. */
      justify-content: center !important;
    }
    .footer {
      display: flex !important;
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
      align-items: center !important;
      gap: 8px !important;
      text-align: left !important;
    }
    .footer .label,
    .footer .link {
      white-space: nowrap !important;
    }
  }
`;
